import React from 'react';
import { orderBy, partition } from 'lodash';
import PropTypes from 'prop-types';
import { OpeningPaidAddonEnum, OpeningVisibilityEnum } from '@src/graphql/generated';
import { OPENING_PAID_ADDONS_BY_TYPE, STATIC_MEDIA_DOMAIN } from '@src/constants';
import { Addon } from './Addon';
import { Preview } from './Addon/Preview';
import './styles.scss';

export const OPENING_PAID_ADDON_ITEMS = [
  {
    contentText: (
      <span>
        Build a stronger brand awareness by reaching out to a wider audience for a longer period. The Premium promotion
        lasts 10 days and includes Facebook & Instagram ads, posts and stories.
      </span>
    ),
    icons: OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.SOCIAL_MEDIA_PREMIUM]?.icons,
    preview: (
      <Preview
        description="Build even stronger brand awareness by reaching out to a wider audience for a longer period. The Premium promotion lasts 10 days and includes Facebook & Instagram posts and stories."
        imageSrc={`${STATIC_MEDIA_DOMAIN}/images/premiumService/social-premium.png`}
        title={OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.SOCIAL_MEDIA_PREMIUM]?.title}
      />
    ),
    title: <span>{OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.SOCIAL_MEDIA_PREMIUM]?.title}</span>,
    type: OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.SOCIAL_MEDIA_PREMIUM]?.type,
  },
  {
    contentText: (
      <span>
        Make your job opening accessible to passive talent using the MeetFrank app. Not only will your opening appear in
        search results, but our specialized algorithm will also ensure it's presented to the right candidates.
      </span>
    ),
    icons: OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.PUBLIC_OPENING]?.icons,
    title: <span>{OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.PUBLIC_OPENING]?.title}</span>,
    type: OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.PUBLIC_OPENING]?.type,
  },
];

const orderPaidAddons = openingPaidAddons => {
  const result = [];

  for (let index = 0; index < openingPaidAddons.length; index++) {
    result.push({
      ...openingPaidAddons[index],
      activePrice: openingPaidAddons[index].discountedPriceInEur
        ? openingPaidAddons[index].discountedPriceInEur
        : openingPaidAddons[index].priceInEur,
    });
  }

  return result;
};

const isSelected = (addon, selectedAddons) => {
  return selectedAddons && selectedAddons.some(selectedAddon => selectedAddon.type === addon.type);
};

export const OpeningServices = ({ opening, paidAddons, setSelectedAddons }) => {
  const { paidAddons: selectedAddons } = opening;
  const [recurringOpeningPaidAddons, openingPaidAddons] = partition(paidAddons, 'isRecurring');

  const virtualRecruiterAddonItem = OPENING_PAID_ADDON_ITEMS.find(
    ({ type }) => type === OpeningPaidAddonEnum.VIRTUAL_RECRUITER,
  );
  const publicOpeningAddonItem = OPENING_PAID_ADDON_ITEMS.find(
    ({ type }) => type === OpeningPaidAddonEnum.PUBLIC_OPENING,
  );

  return (
    <div className="review-premium-services">
      <div className="review-premium-services__heading">Services</div>
      <div className="review-premium-services__info">
        Please select the actions you would like us to perform from the options below.
        <br />
      </div>

      {!!virtualRecruiterAddonItem && (
        <Addon
          addon={{
            isIncluded: true,
            type: 'VIRTUAL_RECRUITER',
          }}
          contentText={virtualRecruiterAddonItem.contentText}
          icons={virtualRecruiterAddonItem.icons}
          isSelected={true}
          key={virtualRecruiterAddonItem.type}
          onClick={setSelectedAddons}
          preview={virtualRecruiterAddonItem.preview}
          title={virtualRecruiterAddonItem.title}
        />
      )}

      {!!publicOpeningAddonItem && (
        <Addon
          addon={{
            discountedPriceInEur: null,
            type: 'PUBLIC_OPENING',
          }}
          contentText={publicOpeningAddonItem.contentText}
          icons={publicOpeningAddonItem.icons}
          isSelected={opening.visibility === OpeningVisibilityEnum.PUBLIC}
          key={publicOpeningAddonItem.type}
          onClick={setSelectedAddons}
          preview={publicOpeningAddonItem.preview}
          title={publicOpeningAddonItem.title}
        />
      )}

      <div className="review-premium-services__list">
        {orderBy(orderPaidAddons(openingPaidAddons), ['isIncluded', 'activePrice'], ['desc', 'asc'])
          .filter(
            addon =>
              ![OpeningPaidAddonEnum.PUBLIC_OPENING, OpeningPaidAddonEnum.VIRTUAL_RECRUITER].includes(addon.type),
          )
          .map(addon => {
            const foundAddon = OPENING_PAID_ADDON_ITEMS.find(item => item.type === addon.type);

            if (!foundAddon) {
              return null;
            }

            return (
              <Addon
                addon={addon}
                contentText={foundAddon.contentText}
                icons={foundAddon.icons}
                isSelected={isSelected(addon, selectedAddons)}
                key={addon.type}
                onClick={setSelectedAddons}
                preview={foundAddon.preview}
                title={foundAddon.title}
              />
            );
          })}
      </div>

      {recurringOpeningPaidAddons.length > 0 && (
        <div className="review-premium-services__list">
          {orderBy(orderPaidAddons(recurringOpeningPaidAddons), ['isIncluded', 'activePrice'], ['desc', 'asc']).map(
            addon => {
              const foundAddon = OPENING_PAID_ADDON_ITEMS.find(item => item.type === addon.type);

              if (!foundAddon) {
                return null;
              }

              return (
                <Addon
                  addon={addon}
                  contentText={foundAddon.contentText}
                  icons={foundAddon.icons}
                  isSelected={isSelected(addon, selectedAddons)}
                  key={addon.type}
                  onClick={setSelectedAddons}
                  preview={foundAddon.preview}
                  title={foundAddon.title}
                />
              );
            },
          )}
        </div>
      )}
    </div>
  );
};

OpeningServices.propTypes = {
  opening: PropTypes.object.isRequired,
  paidAddons: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSelectedAddons: PropTypes.func.isRequired,
};
